/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import cn from 'classnames'
import { get, groupBy, isEmpty, keys, find } from 'lodash'

import { loadConfig, createConfig, updateConfig } from '@ordernext/networking-stadium/stadium'
import Remote from '../remote'

import Button from '../components/Button'
import SelectMenusModal from '../components/SelectMenusModal'
import BackChevron from '../components/BackChevron'
import { ReactComponent as CloseIcon } from '../assets/icons/sidebar-modal-close.svg'
import usePrevious from '../hooks/usePrevious'

import { clearCart } from '../actions/cart'
import { currentMenuSelected, menusSucceeded } from '../actions/menus'
import { fetchAttendants } from '../actions/attendant'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { getDeviceSubtype } from '../selectors/config'
import DeviceSubtypes from '../utils/deviceSubtypes'

import styles from './RevenueCenterSetup.module.scss'
import { getAttendants, getFetchAttendantsStatus } from '../selectors/attendant'

const Record = ({ name, serviceType, ...props }) => {
  return (
    <div {...props} className={styles.record}>
      {serviceType && <span style={{fontWeight: 'bold'}}>{`${serviceType}: `}</span>}
      <span>{name}</span>
    </div>
  )
}

const RevenueCenterSetup = ({
  className,
  attendantsExist,
  clearCart,
  createConfig,
  currentDeviceSubtype,
  currentMenuUuid,
  failed,
  fetchAttendants,
  fetchAttendantsStatus,
  hasConfig,
  history,
  loadConfig,
  loading,
  setCurrentMenuId,
  setSelectedMenu,
  updateConfig,
  venueUuid,
  redirectToPin,
  redirectTo,
}) => {
  const classNames = cn(styles.revenueCenterSetup, className)
  const [isModalOpen, toggleModal] = useState(false)
  const [selectedMenuUuid, setSelectedMenuUuid] = useState()
  const [submitted, setSubmitted] = useState(false)
  const [requestStatus, setRequestStatus] = useState('loading')
  const [data, setData] = useState({})
  const [deviceSubtype, setDeviceSubtype] = useState(currentDeviceSubtype)
  const [deviceSubtypeSelected, setDeviceSubtypeSelected] = useState(false)

  const stands = get(data, 'stands', [])
  const menus = get(data, 'menus', [])
  const filteredMenus = menus.filter((menu) => menu?.serviceType?.toLowerCase() === 'pos')
  const groupedMenus = groupBy(filteredMenus, 'standUuid')
  const previousHasConfig = usePrevious(hasConfig)
  const attendantsFetchCompleted = ['succeeded', 'failed'].includes(fetchAttendantsStatus)

  const handleNavigation = () => {
    if (attendantsExist) {
      redirectToPin(fetchAttendantsStatus)
    } else {
      history.replace('/')
    }
  }

  useEffect(() => {
    const hasConfigChanged = previousHasConfig === false && hasConfig === true
    const isValidConfigMode = ['tabs-pos'].includes(currentDeviceSubtype)

    if (hasConfigChanged && isValidConfigMode && attendantsFetchCompleted) {
      handleNavigation()
    }
  }, [hasConfig, fetchAttendantsStatus])

  useEffect(() => {
    clearCart()
    loadConfig()
    fetchAttendants()
    Remote.getRevenueCenters({ venueUuid }).then((res) => {
      setRequestStatus('success')
      setData(res.data)
    }).catch((err) => {
      setRequestStatus('failed')
    })
  }, [])

  useEffect(() => {
    setSelectedMenuUuid(currentMenuUuid)
  }, [currentMenuUuid])

  useEffect(() => {
    if (submitted && !loading && !failed && attendantsFetchCompleted) {
      handleNavigation()
    }
  }, [submitted, loading, failed, history, fetchAttendantsStatus])

  const renderRecords = (records, selectedUuid, onClick) => {
    return records.map((record) => {
      const isSelected = record.uuid === selectedUuid
      const selectedColor = document?.body?.clientWidth > 800 ? '#ffae00' : '#17A141'
      const style = { backgroundColor: isSelected ? selectedColor : undefined }

      return (
        <Record
          key={record.uuid}
          serviceType={record.serviceTypeDisplayName}
          name={record.name}
          style={style}
          onClick={() => onClick(record.uuid)}
        />
      )
    })
  }

  const handleRedirectToPin = () => {
    const configParams = { menuUuids: [], deviceSubtype: deviceSubtype }

    hasConfig ? updateConfig(configParams) : createConfig(configParams)

    redirectToPin()
  }

  const handleDeviceSubtypeSelected = () => {
    if (DeviceSubtypes.isSingleMenu(deviceSubtype)) {
      setDeviceSubtypeSelected(true)
    } else if (DeviceSubtypes.isSuites(deviceSubtype)) {
      handleRedirectToPin()
    } else {
      toggleModal(true)
    }

    Remote.getRevenueCenters({ venueUuid }).then((res) => {
      setRequestStatus('success')
      setData(res.data)
    }).catch((err) => {
      setRequestStatus('failed')
    })
  }

  const renderDeviceTypes = () => {
    const subtype = 'tabs-pos'
    const selectedColor = document?.body?.clientWidth > 800 ? '#ffae00' : '#17A141'
    const style = { backgroundColor: subtype === deviceSubtype ? selectedColor : undefined }

    return (
      <Record
        key={subtype}
        name={DeviceSubtypes.displayName(subtype)}
        style={style}
        onClick={() => setDeviceSubtype(subtype)}
      />
    )
  }

  const renderStatus = () => {
    if (requestStatus === 'loading') return <p>loading...</p>
    if (requestStatus === 'failed') return <p>An error occurred</p>
    if (requestStatus !== 'loading' && isEmpty(groupedMenus)) return <p>No POS menus are available</p>
  }

  const handleDone = (menuUuids = []) => {
    const menuIds = DeviceSubtypes.isSingleMenu(deviceSubtype) ? [selectedMenuUuid] : menuUuids
    const configParams = { menuUuids: menuIds, deviceSubtype: deviceSubtype }

    setCurrentMenuId(menuIds[0])
    const currentMenu = find(filteredMenus, (menu) => menu?.uuid === menuIds[0])
    const currentStand = find(stands, (stand) => stand?.uuid === currentMenu?.standUuid)
    setSelectedMenu(currentMenu, currentStand)
    hasConfig ? updateConfig(configParams) : createConfig(configParams)
    setSubmitted(true)
  }

  const closeModal = () => {
    toggleModal(false)
  }

  const handleGoBack = () => {
    if (redirectTo) {
      history.push(redirectTo)
    } else {
      history.goBack()
    }
  }

  const backButton = document?.body?.clientWidth > 800 ? <BackChevron className={styles.backChevron} onClick={handleGoBack} /> : <CloseIcon className={styles.closeIcon} onClick={handleGoBack} />

  return (
    <>
      <div className={classNames}>
        {backButton}
        <div className={styles.container}>
          {!deviceSubtypeSelected && (
            <div>
              <h1 className={styles.header}>Select Device Type</h1>
              <div className={styles.standMenus}>{renderDeviceTypes()}</div>
            </div>
          )}

          {deviceSubtypeSelected && (
            <div>
              <h1 className={styles.header}>Select Menu</h1>
              <div className={styles.standMenus}>
                {renderStatus()}
                {keys(groupedMenus).map((standUuid) => {
                  const stand = stands.find((stand) => stand.uuid === standUuid)
                  const menus = groupedMenus[standUuid]

                  return (
                    <div key={standUuid} className={styles.stand}>
                      <h2>{stand?.name}</h2>
                      {renderRecords(menus, selectedMenuUuid, setSelectedMenuUuid)}
                    </div>
                  )
                })}
              </div>

            </div>
          )}
        </div>

        <div className={styles.doneContainer}>
          {!deviceSubtypeSelected && (
            <Button disabled={loading} className={styles.doneButton} onClick={handleDeviceSubtypeSelected}>{loading ? 'LOADING...' : 'NEXT'}</Button>
          )}

          {deviceSubtypeSelected && (
            <div>
              <Button disabled={loading} className={styles.backButton} onClick={() => setDeviceSubtypeSelected(false)}>BACK</Button>
              <Button disabled={loading} className={styles.doneButton} onClick={() => handleDone()}>{loading ? 'LOADING...' : 'DONE'}</Button>
            </div>
          )}
        </div>
      </div>

      <SelectMenusModal
        stands={stands}
        menus={groupedMenus}
        key={isModalOpen}
        closeModal={closeModal}
        isOpen={isModalOpen}
        onSubmit={handleDone}
      />
    </>
  )
}

const mapStateToProps = (state, props) => {
  const redirectTo = props?.history?.location?.state?.redirectTo
  const currentDeviceSubtype = getDeviceSubtype(state)
  const currentMenuUuid = get(state, 'config.configuration.menuUuids[0]', undefined)
  const failed = get(state, 'config.remote.failed', false)
  const hasConfig = get(state, 'config.hasConfig', false)
  const loading = get(state, 'config.remote.loading', false)
  const venueUuid = get(state, 'config.venueUuid', process.env.REACT_APP_VENUE_UUID)
  const attendantsExist = !isEmpty(getAttendants(state))
  const fetchAttendantsStatus = getFetchAttendantsStatus(state)

  return {
    attendantsExist,
    currentDeviceSubtype,
    currentMenuUuid,
    failed,
    hasConfig,
    loading,
    venueUuid,
    redirectTo,
    fetchAttendantsStatus,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const replace = get(ownProps, 'history.replace', () => {})

  return {
    loadConfig: (id) => dispatch(loadConfig(id)),
    createConfig: (payload) => dispatch(createConfig(payload)),
    updateConfig: (payload) => dispatch(updateConfig(payload)),
    clearCart: () => {
      dispatch(clearCart())
      dispatch(clearPromotionsOnOrder())
    },
    setCurrentMenuId: (menuId) => dispatch(currentMenuSelected(menuId)),
    redirectToPin: (fetchAttendantsStatus) =>
      replace({
        pathname: "/user-login",
        state: {
          attendantsAlreadyFetched: fetchAttendantsStatus === "succeeded",
        },
      }),

    setSelectedMenu: (menu, stand) => dispatch(menusSucceeded({ data: { stands: [stand], menus: [menu] } })),
    fetchAttendants: () => dispatch(fetchAttendants()),
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RevenueCenterSetup))
